import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { tradingService } from '../../../services/TradingService';
import { PositionSnapshot, PositionStateSnapshot, StrategyPositionSnapshot } from '../../../types/trading';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';
import {
  collectPortfolioSymbolsForLiveTicks,
  normalizeSymbol,
} from '../../../components/trading-dashboard/portfolioValue';
import { useLatestClose } from '../../../components/trading/useLatestClose';
import { useStockMonitorPrevClose } from '../../../components/trading/useStockMonitorPrevClose';
import { sortPositionsByTradeAtDesc, sortStrategyPositionsByUpdatedAtDesc } from './formatters';

export interface UsePositionStateResult {
  cash: number;
  positions: PositionSnapshot[];
  strategyPositions: StrategyPositionSnapshot[];
  loading: boolean;
  error: string | null;
  positionSymbols: string[];
  latestCloseBySymbol: Record<string, import('../../../types/trading').LatestCloseValue | undefined>;
  prevCloseBySymbol: Record<string, import('../../../types/trading').PrevCloseValue | undefined>;
  effectiveMarketIsOpen: boolean | null;
}

export function usePositionState(): UsePositionStateResult {
  const { userUuid, getToken, isAuthenticated } = useAuth();
  const {
    ticksBySymbol,
    isSimulationEnabled,
    retainSymbolSubscription,
    releaseSymbolSubscription,
    subscribeToOrderEvents,
    subscribeToPortfolioStateUpdates,
  } = useOrderTicketNavigation();

  const [cash, setCash] = useState<number>(0);
  const [positions, setPositions] = useState<PositionSnapshot[]>([]);
  const [strategyPositions, setStrategyPositions] = useState<StrategyPositionSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const retainedSymbolsRef = useRef<Set<string>>(new Set());
  const latestPositionVersionRef = useRef<number>(0);
  const orderFillRefreshTimerRef = useRef<number | null>(null);

  const positionSymbols = useMemo(
    () => Array.from(new Set(
      positions
        .map((position) => normalizeSymbol(position.symbol) || position.symbol)
        .filter((symbol) => symbol && symbol !== 'CASH'),
    )),
    [positions],
  );

  const { latestCloseBySymbol, marketIsOpen } = useLatestClose(positionSymbols, 'stock');
  const prevCloseBySymbol = useStockMonitorPrevClose(positionSymbols);
  const effectiveMarketIsOpen = isSimulationEnabled === true ? true : marketIsOpen;

  /* ── Apply incoming position state snapshot ──────────────────────── */

  const applyPositionStateSnapshot = useCallback((incoming: PositionStateSnapshot) => {
    if (incoming.user_uuid !== userUuid) return;
    if (typeof incoming.cash !== 'number' || !Number.isFinite(incoming.cash)) return;

    const nextVersion = typeof incoming.version === 'number' && Number.isFinite(incoming.version)
      ? incoming.version
      : null;
    if (nextVersion != null && nextVersion < latestPositionVersionRef.current) return;
    if (nextVersion != null) latestPositionVersionRef.current = nextVersion;

    setCash(incoming.cash);
    setPositions(sortPositionsByTradeAtDesc(Object.values(incoming.normal_positions ?? {})));
    setStrategyPositions(sortStrategyPositionsByUpdatedAtDesc(Object.values(incoming.strategy_positions ?? {})));
  }, [userUuid]);

  /* ── Backstop refresh after fills ────────────────────────────────── */

  const refreshPositionState = useCallback(async () => {
    if (!isAuthenticated || !userUuid) return;
    try {
      const token = await getToken();
      if (!token) return;
      const latest = await tradingService.getPositionState(userUuid, token);
      applyPositionStateSnapshot(latest);
    } catch {
      // Best-effort refresh for resiliency after fills.
    }
  }, [applyPositionStateSnapshot, getToken, isAuthenticated, userUuid]);

  const scheduleFilledOrderRefresh = useCallback(() => {
    if (orderFillRefreshTimerRef.current != null) {
      window.clearTimeout(orderFillRefreshTimerRef.current);
    }
    orderFillRefreshTimerRef.current = window.setTimeout(() => {
      orderFillRefreshTimerRef.current = null;
      void refreshPositionState();
    }, 150);
  }, [refreshPositionState]);

  /* ── Reset version on user change ────────────────────────────────── */

  useEffect(() => {
    latestPositionVersionRef.current = 0;
  }, [userUuid]);

  /* ── Cleanup debounce timer ──────────────────────────────────────── */

  useEffect(() => {
    return () => {
      if (orderFillRefreshTimerRef.current != null) {
        window.clearTimeout(orderFillRefreshTimerRef.current);
        orderFillRefreshTimerRef.current = null;
      }
    };
  }, []);

  /* ── Load position state on mount ────────────────────────────────── */

  useEffect(() => {
    if (!isAuthenticated || !userUuid) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const posResult = await tradingService.getPositionState(userUuid, token);
        if (!cancelled) applyPositionStateSnapshot(posResult);
      } catch {
        if (!cancelled) setError('Failed to load position state');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAuthenticated, userUuid, getToken, applyPositionStateSnapshot]);

  /* ── Subscribe to portfolio state updates ────────────────────────── */

  useEffect(() => {
    return subscribeToPortfolioStateUpdates(applyPositionStateSnapshot);
  }, [subscribeToPortfolioStateUpdates, applyPositionStateSnapshot]);

  /* ── Backstop refresh after filled orders ────────────────────────── */

  useEffect(() => {
    return subscribeToOrderEvents((event) => {
      if (event.event_type !== 'filled') return;
      scheduleFilledOrderRefresh();
    });
  }, [scheduleFilledOrderRefresh, subscribeToOrderEvents]);

  /* ── Symbol subscriptions for live ticks ──────────────────────────── */

  useEffect(() => {
    const next = new Set<string>(collectPortfolioSymbolsForLiveTicks(positions, strategyPositions));
    const prev = retainedSymbolsRef.current;
    next.forEach((s) => { if (!prev.has(s)) retainSymbolSubscription(s); });
    prev.forEach((s) => { if (!next.has(s)) releaseSymbolSubscription(s); });
    retainedSymbolsRef.current = next;
  }, [positions, strategyPositions, retainSymbolSubscription, releaseSymbolSubscription]);

  /* ── Release all on unmount ──────────────────────────────────────── */

  useEffect(() => {
    return () => {
      retainedSymbolsRef.current.forEach((s) => releaseSymbolSubscription(s));
      retainedSymbolsRef.current.clear();
    };
  }, [releaseSymbolSubscription]);

  return {
    cash,
    positions,
    strategyPositions,
    loading,
    error,
    positionSymbols,
    latestCloseBySymbol,
    prevCloseBySymbol,
    effectiveMarketIsOpen,
  };
}
